import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircleRounded,
  DashboardRounded,
  DownloadRounded,
  ErrorRounded,
  HomeRounded,
  RefreshRounded,
  SupportRounded,
  LoginRounded,
  KeyRounded,
  VerifiedUserRounded,
} from "@mui/icons-material";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Divider,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { formattedDate } from "../../styles/styles";
import logo from "../../assets/nav-lexmom.webp";
import { backend_api } from "../../static/server";
import { toast } from "react-toastify";
import { useAIActivation } from "../../components/AiBotUtils/AIActivationContext";
import {
  clearPendingAISubscriptionIntent,
  getPendingAISubscriptionIntent,
} from "../../components/AiBotUtils/aiSubscriptionIntent";
import { getMySubscription, getMyUsage } from "../../redux/Apps/users/userSlice";
import {
  applyAuthSession,
  getPendingAuthSession,
  getStoredAuthToken,
} from "../../redux/Apps/users/authSession";

const wait = (ms) =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

const resolveErrorMessage = (error, fallbackMessage) => {
  const candidates = [
    error?.response?.data?.message,
    error?.response?.data?.detail,
    error?.response?.data?.error,
    error?.response?.data?.non_field_errors?.[0],
    error?.response?.data?.errors?.[0],
    error?.message,
  ];

  return (
    candidates.find((value) => typeof value === "string" && value.trim() !== "") ||
    fallbackMessage
  );
};

const isAlreadySubscribedMessage = (message) =>
  /already.*(subscribed|active|purchased|exists|owned)/i.test(
    String(message || ""),
  );

const TrialStatusPage = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token, teamMemberLawyerId } = useSelector((state) => state.user);
  const { activateAI } = useAIActivation();
  const hadCommittedAuthAtLoadRef = useRef(
    Boolean(token && typeof token === "string" && token.trim() !== "") ||
      Boolean(getStoredAuthToken()),
  );

  const paymentIntentId = searchParams.get("payment_intent_id");

  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [finalizingDashboard, setFinalizingDashboard] = useState(false);
  const [pendingAIIntent, setPendingAIIntent] = useState(() =>
    getPendingAISubscriptionIntent(),
  );

  const corePaymentData = useMemo(
    () => paymentData?.data || paymentData || {},
    [paymentData],
  );

  // Helper to determine payment method
  const getPaymentMethod = useCallback(() => {
    const id = corePaymentData.payment_id || paymentIntentId || "";
    if (id.startsWith("pay_")) return "razorpay";
    if (id.startsWith("pi_")) return "stripe";
    return corePaymentData.payment_method || "unknown";
  }, [corePaymentData, paymentIntentId]);

  const paymentMethod = getPaymentMethod();

  const getPaymentAmount = () => {
    if (paymentMethod === "stripe") {
      const stripeAmount =
        corePaymentData.payment_response?.amount ||
        corePaymentData.amount_total ||
        0;

      return stripeAmount / 100;
    }

    return parseFloat(corePaymentData.amount) || 0;
  };

  const getPaymentCurrency = () => {
    if (paymentMethod === "stripe") {
      // Prefer currency from payment_response, fallback to top-level or default
      return (
        corePaymentData.payment_response?.currency?.toUpperCase() ||
        corePaymentData.currency?.toUpperCase() ||
        "USD"
      );
    }
    return corePaymentData.currency || "INR";
  };
  // --- END FIX ---

  const getPaidAt = () => {
    if (
      paymentMethod === "stripe" &&
      corePaymentData.payment_response?.created
    ) {
      return new Date(
        corePaymentData.payment_response.created * 1000,
      ).toISOString();
    }
    return corePaymentData.paid_at || corePaymentData.created_at || null;
  };

  const getPaymentStatus = () => {
    const status = (corePaymentData.status || "").toLowerCase();
    if (["succeeded", "paid", "captured", "success"].includes(status))
      return "success";
    if (status === "failed") return "failure";
    return status || "unknown";
  };

  const finalStatus = getPaymentStatus();
  const shouldShowDashboardAccess =
    finalStatus === "success" && hadCommittedAuthAtLoadRef.current;
  const paymentSessionToken = useMemo(() => {
    const committedToken = token || getStoredAuthToken();
    const pendingToken = getPendingAuthSession()?.token || null;

    return committedToken || pendingToken || null;
  }, [token]);

  const setupSessionFromPayment = useCallback(async () => {
    if (hadCommittedAuthAtLoadRef.current) {
      return token || getStoredAuthToken() || null;
    }

    try {
      const storedSession = getPendingAuthSession();
      if (!storedSession) return null;

      return (
        applyAuthSession(dispatch, storedSession, {
          clearPending: true,
        })?.tokenValue || null
      );
    } catch (err) {
      console.error("Session setup error:", err);
      return null;
    }
  }, [dispatch, token]);

  const syncSubscriptionState = useCallback(
    async (activeToken) => {
      let lastError = null;

      for (let attempt = 0; attempt < 4; attempt += 1) {
        try {
          await dispatch(
            getMySubscription({
              token: activeToken,
              teamMemberLawyerId,
              force: true,
            }),
          ).unwrap();

          try {
            await dispatch(
              getMyUsage({ token: activeToken, teamMemberLawyerId }),
            ).unwrap();
          } catch (usageError) {
            toast.warn(
              resolveErrorMessage(
                usageError,
                "Subscription updated but usage details could not be refreshed.",
              ),
            );
          }

          return true;
        } catch (syncError) {
          lastError = syncError;

          if (attempt < 3) {
            await wait(1200);
          }
        }
      }

      throw lastError;
    },
    [dispatch, teamMemberLawyerId],
  );

  const completePendingAIActivation = useCallback(
    async (activeToken) => {
      const intent = getPendingAISubscriptionIntent();

      if (!intent?.botCode) {
        return false;
      }

      let lastError = null;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const response = await axios.post(
            `${backend_api}ai-employees/direct-subscribe/`,
            {
              bot_code: intent.botCode,
            },
            {
              headers: {
                Authorization: `Token ${activeToken}`,
                "Content-Type": "application/json",
              },
            },
          );

          const resolvedAIName =
            response?.data?.data?.bot?.name ||
            response?.data?.bot?.name ||
            intent.aiName ||
            intent.botCode;

          activateAI(resolvedAIName);
          clearPendingAISubscriptionIntent();
          setPendingAIIntent(null);
          toast.success(`${resolvedAIName} is now active in your dashboard.`);
          return true;
        } catch (activationError) {
          const message = resolveErrorMessage(
            activationError,
            `We couldn't activate ${intent.aiName || "your AI"} yet.`,
          );

          if (isAlreadySubscribedMessage(message)) {
            activateAI(intent.aiName || intent.botCode);
            clearPendingAISubscriptionIntent();
            setPendingAIIntent(null);
            toast.success(
              `${intent.aiName || intent.botCode} is ready in your dashboard.`,
            );
            return true;
          }

          lastError = activationError;

          if (attempt < 2) {
            await wait(1200);
          }
        }
      }

      throw lastError;
    },
    [activateAI],
  );

  const handleDashboardAccess = useCallback(async () => {
    if (finalizingDashboard) {
      return;
    }

    setFinalizingDashboard(true);

    try {
      let activeToken = token || getStoredAuthToken();

      if (!activeToken) {
        const pendingSession = getPendingAuthSession();

        if (pendingSession) {
          activeToken =
            applyAuthSession(dispatch, pendingSession, {
              clearPending: true,
            })?.tokenValue || null;
        }
      }

      if (!activeToken) {
        toast.error("Please log in again to access your dashboard.");
        navigate("/login", { replace: true });
        return;
      }

      await syncSubscriptionState(activeToken);

      const hasPendingAI = Boolean(pendingAIIntent?.botCode);
      if (hasPendingAI) {
        await completePendingAIActivation(activeToken);
      }

      toast.success(
        hasPendingAI
          ? `${
              pendingAIIntent?.aiName || "Your AI"
            } is ready. Redirecting you now...`
          : "Your subscription is active. Redirecting you now...",
      );

      navigate(hasPendingAI ? "/dashboard/ai" : "/dashboard", {
        replace: true,
      });
    } catch (dashboardError) {
      toast.error(
        resolveErrorMessage(
          dashboardError,
          pendingAIIntent?.botCode
            ? `Your plan is active, but we couldn't finish activating ${
                pendingAIIntent.aiName || "your AI"
              }. Please try again once more.`
            : "We couldn't refresh your subscription right now. Please try again.",
        ),
      );
    } finally {
      setFinalizingDashboard(false);
    }
  }, [
    completePendingAIActivation,
    dispatch,
    finalizingDashboard,
    navigate,
    pendingAIIntent,
    syncSubscriptionState,
    token,
  ]);

  useEffect(() => {
    const fetchPayment = async () => {
      if (!paymentIntentId) {
        setError("Payment intent ID not found in URL");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const config = { timeout: 10000 };
        if (paymentSessionToken) {
          config.headers = { Authorization: `Token ${paymentSessionToken}` };
        }

        const response = await axios.get(
          `${backend_api}confirm-payment/?razorpay_payment_id=${paymentIntentId}`,
          config,
        );

        if (response.data) {
          setPaymentData(response.data.data || response.data);
          await setupSessionFromPayment();
        } else {
          throw new Error("No payment data received");
        }
      } catch (err) {
        const message =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to confirm payment. Please contact support.";
        setError(message);
      } finally {
        setLoading(false);
        setTimeout(() => setShowReceipt(true), 500);
      }
    };

    fetchPayment();
  }, [paymentIntentId, paymentSessionToken, setupSessionFromPayment]);

  // Format helpers
  const formatAmount = (amount, currency = "INR") => {
    try {
      const num =
        typeof amount === "string" ? parseFloat(amount) : Number(amount);
      if (isNaN(num) || num <= 0) return "N/A";
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency === "USD" ? "USD" : "INR",
        minimumFractionDigits: 2,
      }).format(num);
    } catch {
      return "N/A";
    }
  };

  const formatDate = (dateString) =>
    dateString ? formattedDate(dateString) : "N/A";

  // Status configuration
  const statusConfig = useMemo(
    () => ({
      success: {
        icon: CheckCircleRounded,
        color: theme.palette.success.main,
        title: "Payment Successful!",
        subtitle: "Your lifetime access is now active",
        gradient: `linear-gradient(135deg, ${alpha(
          theme.palette.success.main,
          0.1,
        )}, ${alpha(theme.palette.background.paper, 0.9)})`,
      },
      failure: {
        icon: ErrorRounded,
        color: theme.palette.error.main,
        title: "Payment Failed",
        subtitle: "Your payment could not be processed",
        gradient: `linear-gradient(135deg, ${alpha(
          theme.palette.error.main,
          0.1,
        )}, ${alpha(theme.palette.background.paper, 0.9)})`,
      },
      unknown: {
        icon: RefreshRounded,
        color: theme.palette.warning.main,
        title: "Payment Status Unknown",
        subtitle: "We could not verify your payment",
        gradient: `linear-gradient(135deg, ${alpha(
          theme.palette.warning.main,
          0.1,
        )}, ${alpha(theme.palette.background.paper, 0.9)})`,
      },
    }),
    [theme],
  );

  const config = statusConfig[finalStatus] || statusConfig.unknown;
  // Actions based on status
  const handleHome = () => navigate("/");
  const handleTryAgain = () => navigate("/pricing");
  const handleContactSupport = () => navigate("/support");

  // Receipt download
  const receiptRef = useRef();

  const downloadPDF = () => {
    const input = receiptRef.current;
    html2canvas(input, {
      scale: 2,
      ignoreElements: (element) => element.classList.contains("no-print"),
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const newWidth = imgWidth * ratio;
      const newHeight = imgHeight * ratio;
      pdf.addImage(imgData, "PNG", 0, 0, newWidth, newHeight);
      pdf.save(`LexMom-Receipt_${paymentIntentId}.pdf`);
    });
  };

  // Reusable detail row
  const DetailBox = ({ title, value, full = false }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {title}:
      </Typography>
      <Typography
        variant="body2"
        fontWeight="medium"
        sx={{ wordBreak: full ? "break-word" : "normal", textAlign: "right" }}
      >
        {value ?? "—"}
      </Typography>
    </Box>
  );

  // Receipt component
  const ReceiptCard = () => {
    const order = corePaymentData.order || {};
    const lawyer = order.lawyer || {};

    const amount = getPaymentAmount();
    const currency = getPaymentCurrency();
    const paidAt = getPaidAt();
    const customerName =
      lawyer.first_name && lawyer.last_name
        ? `${lawyer.first_name} ${lawyer.last_name}`
        : order.guest_name || "—";
    const email =
      lawyer.reminder_mail || lawyer.email || order.guest_email || "—";
    const mobile = lawyer.mobile || "—";

    return (
      <Box ref={receiptRef} sx={{ px: 1, width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            background: "#fff",
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <img src={logo} alt="LexMom" style={{ height: 40 }} />
            <Tooltip title="Download Receipt" className="no-print">
              <IconButton onClick={downloadPDF} size="small">
                <DownloadRounded />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              {formatAmount(amount, currency)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {finalStatus === "success"
                ? "Payment completed"
                : "Payment receipt"}
            </Typography>
          </Box>

          <Stack spacing={1} divider={<Divider sx={{ my: 1 }} />}>
            <Box>
              <DetailBox title="Payment ID" value={paymentIntentId} full />
              <DetailBox title="Date" value={formatDate(paidAt)} />
              <DetailBox title="Method" value={paymentMethod} />
              <DetailBox title="Status" value={finalStatus} />
              {corePaymentData.coupon_codes && (
                <DetailBox
                  title="Coupon"
                  value={corePaymentData.coupon_codes}
                />
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Customer Details
              </Typography>
              <DetailBox title="Name" value={customerName} full />
              <DetailBox title="Email" value={email} full />
              <DetailBox title="Phone" value={mobile} />
            </Box>

            {order.item?.subscription_plan && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Subscription Details
                </Typography>
                <DetailBox
                  title="Plan"
                  value={order.item.subscription_plan.name}
                />
                <DetailBox title="Billing" value="One-time payment" />
              </Box>
            )}
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", textAlign: "center", mt: 2 }}
          >
            Thank you for your purchase! This is a computer‑generated receipt.
          </Typography>
        </Paper>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="body1" color="text.secondary">
          Confirming your payment...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        gap: { xs: 2, md: 4 },
        flexDirection: { xs: "column", lg: "row" },
        p: 2,
      }}
    >
      {/* Status & Actions */}
      <Box sx={{ flex: 1, p: 2 }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Chip
            label={finalStatus.toUpperCase()}
            sx={{
              mb: 2,
              backgroundColor: alpha(config.color, 0.1),
              color: config.color,
              fontWeight: 600,
            }}
          />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {config.title}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {config.subtitle}
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: config.gradient,
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Payment ID
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {paymentIntentId}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Amount
                </Typography>
                <Typography variant="h6">
                  {formatAmount(getPaymentAmount(), getPaymentCurrency())}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </motion.div>

        {/* --- UPDATED: Buttons and guidance for success case --- */}
        {finalStatus === "success" ? (
          shouldShowDashboardAccess ? (
            <>
              <Alert severity="success" sx={{ mb: 3 }}>
                {pendingAIIntent?.botCode
                  ? `${
                      pendingAIIntent.aiName || "Your selected AI"
                    } will be subscribed and activated as soon as you continue to the dashboard.`
                  : "Your payment is confirmed. Continue to the dashboard to refresh your subscription access."}
              </Alert>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  startIcon={<DashboardRounded />}
                  onClick={handleDashboardAccess}
                  disabled={finalizingDashboard}
                >
                  {finalizingDashboard
                    ? "Finalizing your access..."
                    : "Access your dashboard"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HomeRounded />}
                  onClick={() => navigate("/")}
                  disabled={finalizingDashboard}
                >
                  Home
                </Button>
                <Button
                  variant="text"
                  startIcon={<SupportRounded />}
                  onClick={() =>
                    window.open(
                      "https://support.lexmom.com/en/",
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                  disabled={finalizingDashboard}
                >
                  Contact Support
                </Button>
              </Box>
            </>
          ) : (
            <>
            {/* Two‑card guidance for login */}
              <Box sx={{ mb: 3 }}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  <VerifiedUserRounded />
                  New User?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your account has been created. Please log in and use the{" "}
                  <strong>“Forgot Password”</strong> option to set your
                  password.
                </Typography>
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderColor: alpha(theme.palette.success.main, 0.3),
                  bgcolor: alpha(theme.palette.success.main, 0.02),
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="success.main"
                  gutterBottom
                >
                  <KeyRounded /> Existing User?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  If you already have an account, simply log in with your
                  credentials.
                </Typography>
              </Paper>
            </Box>

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                startIcon={<LoginRounded />}
                onClick={() => navigate("/login")}
              >
                Log In to Your Account
              </Button>
              <Button
                variant="outlined"
                startIcon={<HomeRounded />}
                onClick={() => navigate("/")}
              >
                Home
              </Button>
              <Button
                variant="text"
                startIcon={<SupportRounded />}
                onClick={() =>
                  window.open(
                    "https://support.lexmom.com/en/",
                    "_blank",
                    "noopener,noreferrer",
                  )
                }
              >
                Contact Support
              </Button>
            </Box>
            </>
          )
        ) : (
          // For failure or unknown, keep existing buttons
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<RefreshRounded />}
              onClick={handleTryAgain}
            >
              Try Again
            </Button>
            <Button
              variant="outlined"
              startIcon={<SupportRounded />}
              onClick={handleContactSupport}
            >
              Contact Support
            </Button>
            <Button
              variant="text"
              startIcon={<HomeRounded />}
              onClick={handleHome}
            >
              Home
            </Button>
          </Box>
        )}
        {/* --- END UPDATE --- */}
      </Box>

      {/* Receipt (only for success) */}
      {finalStatus === "success" && (
        <Box sx={{ flex: 1, p: 2 }}>
          <AnimatePresence mode="wait">
            {showReceipt ? (
              <motion.div
                key="receipt"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
              >
                <ReceiptCard />
              </motion.div>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={30} />
              </Box>
            )}
          </AnimatePresence>
        </Box>
      )}
    </Box>
  );
};

export default TrialStatusPage;
